import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminRoute from '@/components/AdminRoute';
import ErrorBoundary from './Components/ErrorBoundary';
import SupportChat from './Components/SupportChat';
import PrivateLayout from './Components/Layout';
import PublicLayout from './Components/PublicLayout';

// Public Pages
import LoginPage from './Public/Login/LoginPage';
import SignupPage from './Public/Login/SignupPage';
import ForgotPasswordPage from './Public/ForgotPassword/ForgotPasswordPage';
import ResetPasswordPage from './Public/ResetPassword/ResetPasswordPage';

// Private Pages
import DashboardPage from './Private/Dashboard/DashboardPage';
import TradersPage from './Private/Traders/TradersPage';
import NewToTradePage from './Private/NewToTrade/NewToTradePage';
import TraderDetailsPage from './Private/Traders/TraderDetailsPage';
import SavedTradersPage from './Private/SavedTraders/SavedTradersPage';
import CommodityCodesPage from './Private/CommodityCodes/CommodityCodesPage';
import ProfilePage from './Private/Profile/ProfilePage';
import PlanUsagePage from './Private/Subscription/PlanUsagePage';
import AdminPage from './Private/Admin/AdminPage';
import OrgsPage from './Private/Admin/OrgsPage';
import OrgDetailPage from './Private/Admin/OrgDetailPage';
import EmailsPage from './Private/Admin/Emails/EmailsPage';
import CampaignEditPage from './Private/Admin/Emails/CampaignEditPage';
import DigestsPage from './Private/Admin/Emails/DigestsPage';

const AppRoutes = () => {
    const { isAuthenticated } = useAuth();

    return (
        <Routes>
            {/* Root Route - Conditional Rendering */}
            <Route path="/" element={
                isAuthenticated ? (
                    <PrivateLayout>
                        <DashboardPage />
                    </PrivateLayout>
                ) : (
                    <PublicLayout>
                        <LoginPage />
                    </PublicLayout>
                )
            } />

            {/* Public Routes */}
            <Route path="/login" element={
                <PublicLayout>
                    <LoginPage />
                </PublicLayout>
            } />
            <Route path="/signup" element={
                <PublicLayout>
                    <SignupPage />
                </PublicLayout>
            } />
            <Route path="/forgot-password" element={
                <PublicLayout>
                    <ForgotPasswordPage />
                </PublicLayout>
            } />
            <Route path="/reset-password" element={
                <PublicLayout>
                    <ResetPasswordPage />
                </PublicLayout>
            } />

            {/* Protected Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <DashboardPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />
            
            <Route
                path="/traders"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <TradersPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/new-to-trade"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <NewToTradePage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/traders/:id"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <TraderDetailsPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/saved-traders"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <SavedTradersPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/saved-traders/prospects"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <SavedTradersPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/saved-traders/clients"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <SavedTradersPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/saved-traders/lapsed"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <SavedTradersPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/saved-traders/rejected"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <SavedTradersPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />


            <Route
                path="/commodity-codes"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <CommodityCodesPage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <ProfilePage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/subscription"
                element={
                    <ProtectedRoute>
                        <PrivateLayout>
                            <PlanUsagePage />
                        </PrivateLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <AdminPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/orgs"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <OrgsPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/orgs/:id"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <OrgDetailPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/emails"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <EmailsPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            {/* Before /admin/emails/:id, or the campaign route swallows
                "digests" as an id. */}
            <Route
                path="/admin/emails/digests"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <DigestsPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/emails/digests/:monthId"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <DigestsPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/emails/new"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <CampaignEditPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/emails/:id"
                element={
                    <AdminRoute>
                        <PrivateLayout>
                            <CampaignEditPage />
                        </PrivateLayout>
                    </AdminRoute>
                }
            />
            
        </Routes>
    );
};

const App = () => {
    return (
        <Router>
            <AuthProvider>
                <ThemeProvider>
                    <ErrorBoundary>
                        <AppRoutes />
                        <SupportChat />
                    </ErrorBoundary>
                </ThemeProvider>
            </AuthProvider>
        </Router>
    );
};

export default App; 